import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function OffsiteAttendanceV2({ user, onLogout }) {
  const [meetings, setMeetings] = useState([]);
  const [todayMeeting, setTodayMeeting] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [showCheckInForm, setShowCheckInForm] = useState(false);
  const [showCheckOutForm, setShowCheckOutForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('apply');

  // Application form
  const [applicationData, setApplicationData] = useState({
    client_name: '',
    client_number: '',
    purpose: 'For Sales',
    scheduled_date: new Date().toISOString().split('T')[0],
    scheduled_time: '',
    notes: ''
  });

  // Check-in form
  const [checkInData, setCheckInData] = useState({
    meeting_id: '',
    client_name: '',
    client_number: '',
    client_location_latitude: 0,
    client_location_longitude: 0,
    selfie_with_client_url: '',
    odometer_start_photo_url: '',
    odometer_start_reading: '',
    vehicle_type: '2-wheeler'
  });

  // Check-out form
  const [checkOutData, setCheckOutData] = useState({
    meeting_id: '',
    odometer_end_photo_url: '',
    odometer_end_reading: '',
    meeting_notes: ''
  });

  // Camera states
  const [showCamera, setShowCamera] = useState(false);
  const [cameraType, setCameraType] = useState(''); // 'selfie', 'odometer_start', 'odometer_end'
  const [capturedImage, setCapturedImage] = useState(null);
  const [stream, setStream] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const videoRef = useRef(null);

  useEffect(() => {
    fetchTodayStatus();
    fetchMyMeetings();
  }, []);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const fetchTodayStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/offsite-attendance/meetings/today-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayMeeting(response.data.has_meeting_today ? response.data.meeting : null);
    } catch (error) {
      console.error('Error fetching today status:', error);
    }
  };

  const fetchMyMeetings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/offsite-attendance/meetings/my-meetings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMeetings(response.data.meetings || []);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCheckInData({
            ...checkInData,
            client_location_latitude: position.coords.latitude,
            client_location_longitude: position.coords.longitude
          });
          alert('📍 Location captured successfully!');
        },
        (error) => {
          alert('Error getting location: ' + error.message);
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  // Camera functions
  const openCamera = async (type) => {
    setCameraType(type);
    setShowCamera(true);
    setCapturedImage(null);

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: type === 'selfie' ? 'user' : 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setStream(mediaStream);
    } catch (error) {
      alert('Error accessing camera: ' + error.message);
      setShowCamera(false);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0);
    
    canvas.toBlob(async (blob) => {
      const imageUrl = URL.createObjectURL(blob);
      setCapturedImage(imageUrl);
      await uploadCapturedImage(blob);
    }, 'image/jpeg', 0.8);
  };

  const uploadCapturedImage = async (blob) => {
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', blob, `${cameraType}_${Date.now()}.jpg`);

      const response = await axios.post(
        `${API}/api/file-uploads/attendance-photo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const imageUrl = response.data.file_url;

      if (cameraType === 'selfie') {
        setCheckInData(prev => ({ ...prev, selfie_with_client_url: imageUrl }));
      } else if (cameraType === 'odometer_start') {
        setCheckInData(prev => ({ ...prev, odometer_start_photo_url: imageUrl }));
      } else if (cameraType === 'odometer_end') {
        setCheckOutData(prev => ({ ...prev, odometer_end_photo_url: imageUrl }));
      }

      alert('✅ Photo captured successfully!');
      closeCamera();
    } catch (error) {
      alert('Error uploading photo: ' + (error.response?.data?.detail || error.message));
      setCapturedImage(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
    setCapturedImage(null);
  };

  const handleApplyForMeeting = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/offsite-attendance/meetings/apply`,
        applicationData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert('✅ Meeting application submitted successfully!');
      setShowApplicationForm(false);
      setApplicationData({
        client_name: '',
        client_number: '',
        purpose: 'For Sales',
        scheduled_date: new Date().toISOString().split('T')[0],
        scheduled_time: '',
        notes: ''
      });
      fetchMyMeetings();
      fetchTodayStatus();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();

    if (!checkInData.selfie_with_client_url) {
      alert('❌ Selfie with client is mandatory!');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/offsite-attendance/meetings/check-in`,
        checkInData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      alert(`✅ Checked in successfully!\n\nDistance: ${response.data.total_distance_km}km\nClaimable: ${response.data.claimable_distance_km}km`);
      setShowCheckInForm(false);
      setCheckInData({
        meeting_id: '',
        client_name: '',
        client_number: '',
        client_location_latitude: 0,
        client_location_longitude: 0,
        selfie_with_client_url: '',
        odometer_start_photo_url: '',
        odometer_start_reading: '',
        vehicle_type: '2-wheeler'
      });
      fetchMyMeetings();
      fetchTodayStatus();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/api/offsite-attendance/meetings/check-out`,
        checkOutData,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (response.data.travel_claim_created) {
        alert(`✅ Checked out successfully!\n\n💰 Travel claim created: ₹${response.data.claim_amount}`);
      } else {
        alert('✅ Checked out successfully!\n\n' + response.data.reason);
      }
      
      setShowCheckOutForm(false);
      setCheckOutData({
        meeting_id: '',
        odometer_end_photo_url: '',
        odometer_end_reading: '',
        meeting_notes: ''
      });
      fetchMyMeetings();
      fetchTodayStatus();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const startCheckIn = (meeting) => {
    setCheckInData({
      meeting_id: meeting.id,
      client_name: meeting.client_name || '',
      client_number: meeting.client_number || '',
      client_location_latitude: 0,
      client_location_longitude: 0,
      selfie_with_client_url: '',
      odometer_start_photo_url: '',
      odometer_start_reading: '',
      vehicle_type: '2-wheeler'
    });
    setShowCheckInForm(true);
  };

  const startCheckOut = (meeting) => {
    setCheckOutData({
      meeting_id: meeting.id,
      odometer_end_photo_url: '',
      odometer_end_reading: '',
      meeting_notes: ''
    });
    setShowCheckOutForm(true);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Applied': return 'bg-blue-100 text-blue-800';
      case 'Checked-In': return 'bg-green-100 text-green-800';
      case 'Completed': return 'bg-gray-100 text-gray-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Offsite Meetings & Visits</h1>
          <p className="mt-2 text-gray-600">Manage client meetings with selfie verification and auto travel claims</p>
        </div>

        {/* Today's Status */}
        {todayMeeting && (
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-lg p-6 mb-6 text-white">
            <h2 className="text-xl font-bold mb-3">Today's Meeting</h2>
            <div className="bg-white bg-opacity-20 rounded-lg p-4">
              <p className="font-semibold">{todayMeeting.client_name || 'Client TBD'}</p>
              <p className="text-sm mt-1">Purpose: {todayMeeting.purpose}</p>
              <p className="text-sm mt-1">Status: {todayMeeting.status}</p>
              {todayMeeting.status === 'Applied' && (
                <button
                  onClick={() => startCheckIn(todayMeeting)}
                  className="mt-3 px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-gray-100 transition"
                >
                  Check In Now
                </button>
              )}
              {todayMeeting.status === 'Checked-In' && (
                <button
                  onClick={() => startCheckOut(todayMeeting)}
                  className="mt-3 px-4 py-2 bg-white text-purple-600 rounded-lg hover:bg-gray-100 transition"
                >
                  Check Out Now
                </button>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('apply')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'apply' ? 'bg-[#2b3380] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Apply for Meeting
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-2 rounded-lg transition ${
              activeTab === 'history' ? 'bg-[#2b3380] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Meeting History
          </button>
        </div>

        {/* Apply Tab */}
        {activeTab === 'apply' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-bold mb-4">Apply for Client Meeting</h2>
            <button
              onClick={() => setShowApplicationForm(true)}
              className="px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition"
            >
              + Apply for Meeting
            </button>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-4 border-b">
              <h2 className="text-xl font-bold">Meeting History</h2>
            </div>
            {meetings.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>No meetings found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purpose</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Distance</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {meetings.map((meeting) => (
                      <tr key={meeting.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {new Date(meeting.scheduled_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {meeting.client_name || 'TBD'}
                        </td>
                        <td className="px-6 py-4 text-sm">{meeting.purpose}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(meeting.status)}`}>
                            {meeting.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {meeting.total_distance_km ? `${meeting.total_distance_km} km` : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {meeting.status === 'Applied' && (
                            <button
                              onClick={() => startCheckIn(meeting)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Check In
                            </button>
                          )}
                          {meeting.status === 'Checked-In' && (
                            <button
                              onClick={() => startCheckOut(meeting)}
                              className="text-green-600 hover:text-green-900"
                            >
                              Check Out
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Application Form Modal */}
        {showApplicationForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Apply for Meeting</h2>
                  <button onClick={() => setShowApplicationForm(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
                </div>

                <form onSubmit={handleApplyForMeeting} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Purpose *</label>
                    <select
                      required
                      value={applicationData.purpose}
                      onChange={(e) => setApplicationData({...applicationData, purpose: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                    >
                      <option value="For Sales">For Sales</option>
                      <option value="For Franchisee">For Franchisee</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Date *</label>
                    <input
                      type="date"
                      required
                      value={applicationData.scheduled_date}
                      onChange={(e) => setApplicationData({...applicationData, scheduled_date: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Time</label>
                    <input
                      type="time"
                      value={applicationData.scheduled_time}
                      onChange={(e) => setApplicationData({...applicationData, scheduled_time: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client Name (optional - can fill at check-in)</label>
                    <input
                      type="text"
                      value={applicationData.client_name}
                      onChange={(e) => setApplicationData({...applicationData, client_name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      placeholder="Can be filled later"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client Number (optional - can fill at check-in)</label>
                    <input
                      type="tel"
                      value={applicationData.client_number}
                      onChange={(e) => setApplicationData({...applicationData, client_number: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      placeholder="Can be filled later"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={applicationData.notes}
                      onChange={(e) => setApplicationData({...applicationData, notes: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      rows="2"
                      placeholder="Additional notes"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowApplicationForm(false)}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition disabled:bg-gray-400"
                    >
                      {loading ? 'Submitting...' : 'Apply'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Check-In Modal */}
        {showCheckInForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Check In at Client Location</h2>
                  <button onClick={() => setShowCheckInForm(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
                </div>

                <form onSubmit={handleCheckIn} className="space-y-4">
                  {/* Selfie - MANDATORY */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Selfie WITH CLIENT * <span className="text-red-500">(Mandatory)</span>
                    </label>
                    {checkInData.selfie_with_client_url ? (
                      <div className="relative">
                        <img src={checkInData.selfie_with_client_url} alt="Selfie" className="w-full h-48 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => openCamera('selfie')}
                          className="absolute top-2 right-2 px-3 py-1 bg-white text-[#2b3380] rounded-lg shadow-lg hover:bg-gray-100"
                        >
                          Retake
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openCamera('selfie')}
                        className="w-full px-4 py-8 border-2 border-dashed border-red-300 rounded-lg hover:border-[#2b3380] transition text-center"
                      >
                        <div className="text-5xl mb-2">🤳</div>
                        <p className="text-sm font-medium text-gray-700">Take selfie with client</p>
                        <p className="text-xs text-red-600 mt-1">Required for check-in</p>
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                    <input
                      type="text"
                      required
                      value={checkInData.client_name}
                      onChange={(e) => setCheckInData({...checkInData, client_name: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Client Number *</label>
                    <input
                      type="tel"
                      required
                      value={checkInData.client_number}
                      onChange={(e) => setCheckInData({...checkInData, client_number: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GPS Location</label>
                    <button
                      type="button"
                      onClick={getCurrentLocation}
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                      📍 Capture Current Location
                    </button>
                    {checkInData.client_location_latitude !== 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        ✅ Location captured: {checkInData.client_location_latitude.toFixed(4)}, {checkInData.client_location_longitude.toFixed(4)}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Type *</label>
                    <select
                      required
                      value={checkInData.vehicle_type}
                      onChange={(e) => setCheckInData({...checkInData, vehicle_type: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                    >
                      <option value="2-wheeler">🛵 2-Wheeler (₹3/km)</option>
                      <option value="4-wheeler">🚗 4-Wheeler (₹6.50/km)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Odometer Photo (optional)</label>
                    {checkInData.odometer_start_photo_url ? (
                      <div className="relative">
                        <img src={checkInData.odometer_start_photo_url} alt="Odometer" className="w-full h-48 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => openCamera('odometer_start')}
                          className="absolute top-2 right-2 px-3 py-1 bg-white text-[#2b3380] rounded-lg shadow-lg hover:bg-gray-100"
                        >
                          Retake
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openCamera('odometer_start')}
                        className="w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#2b3380] transition text-center"
                      >
                        <div className="text-4xl mb-1">📷</div>
                        <p className="text-sm font-medium text-gray-700">Capture start odometer</p>
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Odometer Reading</label>
                    <input
                      type="number"
                      step="0.1"
                      value={checkInData.odometer_start_reading}
                      onChange={(e) => setCheckInData({...checkInData, odometer_start_reading: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      placeholder="Enter reading"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCheckInForm(false)}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading || !checkInData.selfie_with_client_url}
                      className="flex-1 px-6 py-3 bg-[#2b3380] text-white rounded-lg hover:bg-[#1f2660] transition disabled:bg-gray-400"
                    >
                      {loading ? 'Checking In...' : 'Check In'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Check-Out Modal */}
        {showCheckOutForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Check Out from Meeting</h2>
                  <button onClick={() => setShowCheckOutForm(false)} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
                </div>

                <form onSubmit={handleCheckOut} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Odometer Photo (optional)</label>
                    {checkOutData.odometer_end_photo_url ? (
                      <div className="relative">
                        <img src={checkOutData.odometer_end_photo_url} alt="Odometer" className="w-full h-48 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => openCamera('odometer_end')}
                          className="absolute top-2 right-2 px-3 py-1 bg-white text-[#2b3380] rounded-lg shadow-lg hover:bg-gray-100"
                        >
                          Retake
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openCamera('odometer_end')}
                        className="w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#2b3380] transition text-center"
                      >
                        <div className="text-4xl mb-1">📷</div>
                        <p className="text-sm font-medium text-gray-700">Capture end odometer</p>
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Odometer Reading</label>
                    <input
                      type="number"
                      step="0.1"
                      value={checkOutData.odometer_end_reading}
                      onChange={(e) => setCheckOutData({...checkOutData, odometer_end_reading: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      placeholder="Enter reading"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Notes</label>
                    <textarea
                      value={checkOutData.meeting_notes}
                      onChange={(e) => setCheckOutData({...checkOutData, meeting_notes: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2b3380]"
                      rows="3"
                      placeholder="Summary of meeting"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCheckOutForm(false)}
                      className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:bg-gray-400"
                    >
                      {loading ? 'Checking Out...' : 'Check Out'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Camera Modal */}
        {showCamera && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
            <div className="w-full max-w-2xl p-4">
              <div className="bg-white rounded-lg overflow-hidden">
                <div className="p-4 bg-gray-800 text-white flex justify-between items-center">
                  <h3 className="text-lg font-semibold">
                    {cameraType === 'selfie' ? '🤳 Selfie with Client' : '📷 Capture Odometer'}
                  </h3>
                  <button onClick={closeCamera} className="text-white hover:text-gray-300 text-2xl">×</button>
                </div>

                <div className="relative bg-black">
                  {!capturedImage ? (
                    <video ref={videoRef} autoPlay playsInline className="w-full" style={{ maxHeight: '60vh' }} />
                  ) : (
                    <img src={capturedImage} alt="Captured" className="w-full" style={{ maxHeight: '60vh' }} />
                  )}
                </div>

                <div className="p-4 flex gap-3 justify-center">
                  {!capturedImage ? (
                    <button
                      onClick={capturePhoto}
                      disabled={uploadingImage}
                      className="px-8 py-3 bg-[#2b3380] text-white rounded-full hover:bg-[#1f2660] transition text-lg font-semibold disabled:bg-gray-400"
                    >
                      📸 Capture
                    </button>
                  ) : (
                    <button
                      disabled={uploadingImage}
                      className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:bg-gray-400"
                    >
                      {uploadingImage ? 'Uploading...' : '✓ Photo Captured'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default OffsiteAttendanceV2;